import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { publicAppointmentActionsApi } from '@/features/okdoc/api/publicAppointmentActions';
import { ClinicContactsCard } from '@/features/okdoc/components/ClinicContactsCard';
import { OkdocShell } from '@/features/okdoc/components/OkdocShell';
import { PatientPortalCard } from '@/features/okdoc/components/PatientPortalCard';
import { okdocBrandThemes, okdocDefaultTheme, okdocPatientPortals } from '@/features/okdoc/config';
import { AppointmentConfirmationLanding } from '@/features/okdoc/flows/appointment-confirmation/AppointmentConfirmationLanding';
import { FeedbackCollectionLanding } from '@/features/okdoc/flows/feedback-collection/FeedbackCollectionLanding';
import { RecommendationsLanding } from '@/features/okdoc/flows/recommendations/RecommendationsLanding';
import { getOkdocShellCopy, resolvePublicScenarioProfile } from '@/features/okdoc/utils/scenarioProfile';

const publicOkdocMetaTitle = 'Полезная информация';

// 19.03.26 2.: единый public router для всех OKDoc flow.
// Связанный док: docs/living/okdoc/OKDOC_SCENARIOS_FOUNDATION.md
// При изменении router/flow UI обновляйте и код, и док.
export function OkdocLandingPage() {
  const params = useParams();
  const code = useMemo(() => (params.code || params.publicCode || '').trim(), [params.code, params.publicCode]);
  const [submitStatus, setSubmitStatus] = useState<string>('idle');
  const [submitAction, setSubmitAction] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');

  const landingQuery = useQuery({
    queryKey: ['okdoc-landing', code],
    queryFn: () => publicAppointmentActionsApi.getLanding(code),
    enabled: code.length > 0,
    retry: 1,
  });

  const landing = landingQuery.data;
  const clinicName = landing?.clinic_name || landing?.tenant_name || 'Клиника';
  const theme = okdocBrandThemes[landing?.tenant_slug || ''] || okdocDefaultTheme;
  const patientPortal = landing?.tenant_slug ? okdocPatientPortals[landing.tenant_slug] : undefined;
  const flow = landing?.flow || 'appointment_confirmation';
  const resolvedProfile = landing ? resolvePublicScenarioProfile(landing) : undefined;
  const isBirthdayOffer = landing?.scenario_kind === 'birthday_offer';
  const hidesStandalonePatientPortalCard =
    landing?.scenario_kind === 'birthday_offer' ||
    (landing?.scenario_kind === 'contact_hub' && landing?.tenant_slug !== 'implant-beauty');
  const shellCopy = getOkdocShellCopy(flow, resolvedProfile);
  const isFeedbackFlow = flow === 'feedback_collection';
  const isRedirectFlow = flow === 'external_redirect';
  const isRecommendationsFlow = flow === 'recommendations';
  const scenarioCTACount = landing?.scenario_profile?.ctas?.length || 0;
  // 27.03.26: если scenario CTA в данных нет, используем tenant-level ЛК пациента как fallback-действие.
  const usePatientPortalAsRecommendationsCTA = isRecommendationsFlow && scenarioCTACount === 0 && Boolean(patientPortal?.url);

  useEffect(() => {
    const previousTitle = document.title;
    const metaTargets = [
      { selector: 'meta[name="description"]', attr: 'name', value: 'description', content: 'Patient-facing уведомления и материалы для пациента.' },
      { selector: 'meta[property="og:title"]', attr: 'property', value: 'og:title', content: publicOkdocMetaTitle },
      { selector: 'meta[property="og:description"]', attr: 'property', value: 'og:description', content: 'Patient-facing уведомления и материалы для пациента.' },
      { selector: 'meta[name="twitter:title"]', attr: 'name', value: 'twitter:title', content: publicOkdocMetaTitle },
      { selector: 'meta[name="twitter:description"]', attr: 'name', value: 'twitter:description', content: 'Patient-facing уведомления и материалы для пациента.' },
    ];

    const previousMeta = metaTargets.map((target) => {
      const existing = document.head.querySelector<HTMLMetaElement>(target.selector);
      return {
        ...target,
        existed: Boolean(existing),
        previousContent: existing?.getAttribute('content') || '',
      };
    });

    document.title = publicOkdocMetaTitle;
    for (const target of metaTargets) {
      let meta = document.head.querySelector<HTMLMetaElement>(target.selector);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(target.attr, target.value);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', target.content);
    }

    return () => {
      document.title = previousTitle;
      for (const snapshot of previousMeta) {
        const meta = document.head.querySelector<HTMLMetaElement>(snapshot.selector);
        if (!meta) {
          continue;
        }
        if (!snapshot.existed) {
          meta.remove();
          continue;
        }
        meta.setAttribute('content', snapshot.previousContent);
      }
    };
  }, []);

  useEffect(() => {
    if (!isRedirectFlow || !landing?.redirect_url || landing.status !== 'ready') {
      return;
    }

    const timer = window.setTimeout(() => {
      window.location.assign(publicAppointmentActionsApi.getTrackedRedirectUrl(code, 'default'));
    }, 400);

    return () => window.clearTimeout(timer);
  }, [code, isRedirectFlow, landing?.redirect_url, landing?.status]);

  // 19.03.26 2.: единый submit-хендлер для flow, где нужен patient action.
  const handleAction = async (action: string) => {
    if (!code) return;
    setSubmitStatus('loading');
    setSubmitAction(action);
    setSubmitError('');
    try {
      const result = await publicAppointmentActionsApi.submit(code, action);
      if (result.status === 'success') {
        setSubmitStatus('success');
        return;
      }
      if (result.status === 'used') {
        setSubmitStatus('used');
        return;
      }
      if (result.status === 'expired') {
        setSubmitStatus('expired');
        return;
      }
      setSubmitStatus('error');
      setSubmitError('Не удалось зафиксировать действие. Попробуйте ещё раз.');
    } catch (error: any) {
      setSubmitStatus('error');
      setSubmitError(error?.response?.data?.error || 'Не удалось зафиксировать действие. Попробуйте ещё раз.');
    }
  };

  // 19.03.26 2.: выбирает готовый UI-блок под конкретный OKDoc flow.
  const renderReadyFlow = () => {
    if (!landing) {
      return null;
    }
    if (isRedirectFlow) {
      return (
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center text-slate-700 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
          <p className="text-xl font-semibold text-slate-900">Переходим по короткой ссылке</p>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            Если автоматический переход не сработал, нажмите кнопку ниже.
          </p>
          {landing.redirect_url ? (
            <Button
              className={`mt-6 w-full rounded-2xl px-5 py-4 text-base shadow-sm ${theme.primaryButton}`}
              size="lg"
              onClick={() => window.location.assign(publicAppointmentActionsApi.getTrackedRedirectUrl(code, 'default'))}
            >
              {landing.redirect_label || 'Открыть ссылку'}
            </Button>
          ) : (
            <div className="mt-6 rounded-2xl border border-amber-100 bg-amber-50 p-4 text-sm text-amber-800">
              Не удалось определить адрес для перехода.
            </div>
          )}
        </div>
      );
    }
    if (isFeedbackFlow) {
      return (
        <FeedbackCollectionLanding
          code={code}
          landing={landing}
          theme={theme}
          submitStatus={submitStatus}
          submitAction={submitAction}
          submitError={submitError}
          onAction={handleAction}
        />
      );
    }
    if (isRecommendationsFlow) {
      return (
        <RecommendationsLanding
          code={code}
          landing={landing}
          theme={theme}
          onRedirect={(target, options) => {
            const trackedUrl = publicAppointmentActionsApi.getTrackedRedirectUrl(code, target || 'default');
            if (options?.openInNewTab) {
              window.open(trackedUrl, '_blank', 'noopener,noreferrer');
              return;
            }
            window.location.assign(trackedUrl);
          }}
          patientPortal={usePatientPortalAsRecommendationsCTA ? patientPortal : undefined}
        />
      );
    }
    return (
      <AppointmentConfirmationLanding
        landing={landing}
        theme={theme}
        submitStatus={submitStatus}
        submitAction={submitAction}
        submitError={submitError}
        onAction={handleAction}
      />
    );
  };

  return (
    <OkdocShell
      title={shellCopy.title}
      clinicName={isBirthdayOffer ? '' : clinicName}
      theme={theme}
      subtitle={shellCopy.subtitle}
    >
      {landingQuery.isLoading && (
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-[0_24px_60px_-36px_rgba(15,23,42,0.35)]">
          Загружаем данные ссылки...
        </div>
      )}

      {landingQuery.isError && (
        <div className="rounded-[28px] border border-red-100 bg-red-50 p-8 text-center text-red-700">
          Не удалось загрузить ссылку. Попробуйте открыть её ещё раз.
        </div>
      )}

      {landing && !landingQuery.isLoading && !landingQuery.isError && (
        <>
          {(landing.status === 'not_found' || landing.status === 'expired') && (
            <div className="rounded-[28px] border border-amber-100 bg-amber-50 p-8 text-center text-amber-800">
              Ссылка недействительна или срок её действия уже истёк.
            </div>
          )}

          {(landing.status === 'ready' || submitStatus === 'success' || submitStatus === 'loading' || submitStatus === 'error' || submitStatus === 'used') && renderReadyFlow()}

          {!isFeedbackFlow && !isRedirectFlow && patientPortal && !usePatientPortalAsRecommendationsCTA && !hidesStandalonePatientPortalCard && (
            <PatientPortalCard
              portal={patientPortal}
              headerClassName={theme.header}
              accentClassName={theme.accent}
              buttonClassName={theme.primaryButton}
            />
          )}

          <ClinicContactsCard landing={landing} accentClassName={theme.accent} />
        </>
      )}
    </OkdocShell>
  );
}
